<script setup lang="ts">
import { useRouter } from "vue-router";
import { useSession } from "@/stores/session";
import { useSelectOrganisation } from "@/stores/selectOrganisation";
import { useRefreshComponent } from "@/stores/refreshComponent";
import { onMounted, ref } from "vue";
import type { Organisation } from "@/types";
import { AuthAPI, OrganisationAPI } from "@/api";

const router = useRouter();
const {
  currentOrganisation,
  currentTopOrganisation,
  setTopOrganisation,
  initTopOrganisationFromCookie,
  initSubOrganisationFromCookie,
} = useSelectOrganisation();
const { refreshTitleBarKey } = useRefreshComponent();

const { isSuperAdmin } = useSession();

const organisations = ref<Organisation[]>([]);
const topLevelOrganisations = ref<Organisation[]>([]);

const logout = async () => {
  await AuthAPI.logout();
  router.push("/login");
};

onMounted(async () => {
  organisations.value = await OrganisationAPI.fetchAll();
  await initTopOrganisationFromCookie(organisations.value);
  topLevelOrganisations.value = organisations.value.filter(
    (o) => o.organisation_parent_id === null
  );
  await initSubOrganisationFromCookie(organisations.value);
  // when logging in as admin
  if (topLevelOrganisations.value.length <= 1 && !isSuperAdmin.value) {
    await setTopOrganisation(
      topLevelOrganisations.value[0] || organisations.value[0]
    );
  }
});
</script>

<template>
  <div :key="refreshTitleBarKey" class="title-bar">
    <div v-if="isSuperAdmin">
      <ElSelect
        v-model="currentTopOrganisation"
        placeholder="Select top organisation"
        class="el-select"
        size="large"
        clearable
        value-key="id"
        @change="useRefreshComponent().refreshTitleBar()"
      >
        <template #prefix>🏢</template>
        <ElOption
          v-for="organisation in topLevelOrganisations"
          :key="organisation.id"
          :label="organisation.name"
          :value="organisation"
        />
      </ElSelect>
    </div>

    <div>
      <ElSelect
        v-model="currentOrganisation"
        placeholder="🏬 Select sub-organisation"
        class="el-select"
        size="large"
        clearable
        :disabled="!currentTopOrganisation"
        @change="useRefreshComponent().refreshNavigationBar()"
      >
        <ElOption
          v-for="organisation in organisations"
          :key="organisation.id"
          :label="organisation.name"
          :value="organisation.id"
        />
      </ElSelect>
    </div>
    <ElButton type="primary" icon="close" size="large" @click="logout">
      Uitloggen
    </ElButton>
  </div>
</template>

<style scoped>
.title-bar {
  @apply w-full h-full flex justify-end items-center space-x-4;
  border-bottom: 1px solid var(--el-border-color-lighter);
}

.el-select {
  @apply w-60;
}
</style>
